<template>
  <AppPage labelledby="profile-page-title">
    <PageHeader
      title-id="profile-page-title"
      title="Мой профиль"
      description="Личные данные, доступ к организациям и безопасность аккаунта."
    >
      <template #status>
        <StatusBadge
          :label="is_confirmed ? 'Аккаунт подтверждён' : 'Нужно подтверждение'"
          :tone="is_confirmed ? 'success' : 'warning'"
          :icon="is_confirmed ? 'verified_user' : 'mark_email_unread'"
        />
      </template>
    </PageHeader>

    <AppState
      v-if="isLoading"
      state="loading"
      title="Загружаем профиль"
      message="Получаем личные данные и доступные организации."
      data-testid="profile-loading"
    />

    <template v-else>
      <PageSection
        title="Личные данные и безопасность"
        description="Эти данные используются в документах и уведомлениях."
      >
        <div class="identity-layout">
          <div class="avatar-control">
            <q-avatar size="96px" color="primary" text-color="white" data-testid="profile-avatar">
              <img v-if="user.avatar" :src="avatarSrc(user.avatar)" alt="Аватар пользователя">
              <span v-else aria-hidden="true">{{ userInitials }}</span>
            </q-avatar>
            <q-file
              ref="avatarInput"
              v-model="newAvatar"
              class="avatar-control__input"
              @update:model-value="onAvatarSelected"
            />
            <div class="avatar-control__actions">
              <q-btn
                outline
                no-caps
                color="primary"
                icon="photo_camera"
                label="Загрузить аватар"
                :loading="isAvatarUploading"
                :disable="isAvatarUploading"
                data-testid="avatar-upload"
                @click="triggerAvatarUpload"
              />
              <q-btn
                v-if="user.avatar"
                flat
                no-caps
                color="negative"
                icon="delete_outline"
                label="Удалить аватар"
                :disable="isAvatarUploading"
                data-testid="avatar-delete"
                @click="deleteAvatar"
              />
            </div>
            <p id="avatar-guidance" class="support-copy">PNG или JPG, не более 5 МБ.</p>
            <p
              v-if="avatarStatus.message"
              class="inline-status"
              :class="`inline-status--${avatarStatus.tone}`"
              role="status"
              data-testid="avatar-status"
            >
              {{ avatarStatus.message }}
            </p>
          </div>

          <div class="identity-details">
            <div class="identity-summary">
              <strong class="identity-summary__name">{{ fullName || 'Имя не указано' }}</strong>
              <span class="identity-summary__email">{{ user.email }}</span>
            </div>

            <div v-if="editMode" class="identity-form" data-testid="profile-edit-form">
              <q-input v-model="editedUser.surname" outlined label="Фамилия" autocomplete="family-name" />
              <q-input v-model="editedUser.name" outlined label="Имя" autocomplete="given-name" />
              <q-input v-model="editedUser.familyName" outlined label="Отчество" autocomplete="additional-name" />
            </div>
            <dl v-else class="definition-list" data-testid="profile-identity-readonly">
              <div><dt>Фамилия</dt><dd>{{ user.surname || '—' }}</dd></div>
              <div><dt>Имя</dt><dd>{{ user.name || '—' }}</dd></div>
              <div><dt>Отчество</dt><dd>{{ user.familyName || '—' }}</dd></div>
            </dl>

            <div class="section-actions" aria-label="Действия с профилем">
              <q-btn
                v-if="editMode"
                flat
                no-caps
                label="Отменить редактирование"
                :disable="profileSaving"
                @click="cancelEdit"
              />
              <q-btn
                color="primary"
                no-caps
                :icon="editMode ? 'save' : 'edit'"
                :label="editMode ? 'Сохранить личные данные' : 'Редактировать личные данные'"
                :loading="profileSaving"
                data-testid="profile-edit-action"
                @click="toggleEditMode"
              />
              <q-btn
                outline
                no-caps
                color="primary"
                icon="password"
                label="Изменить пароль"
                data-testid="change-password-action"
                @click="openPasswordDialog"
              />
            </div>
          </div>
        </div>

        <div v-if="!is_confirmed" class="confirmation-panel" data-testid="account-confirmation">
          <div>
            <h3>Подтвердите адрес электронной почты</h3>
            <p>Введите код из письма, чтобы принимать приглашения и создавать организации.</p>
          </div>
          <div class="confirmation-panel__form">
            <q-input
              v-model="confirmation_code"
              outlined
              mask="# # # # # #"
              label="Код подтверждения"
              inputmode="numeric"
              :loading="code_request_sended"
            />
            <q-btn
              color="primary"
              no-caps
              label="Подтвердить код"
              :disable="!confirmation_code || code_request_sended"
              @click="sendConfirmationCode"
            />
            <q-btn
              flat
              no-caps
              color="primary"
              label="Отправить код повторно"
              :disable="code_request_sended"
              @click="resendConfirmationCode"
            />
          </div>
        </div>
      </PageSection>

      <PageSection
        ref="organizationsSection"
        title="Организации"
        description="Здесь показаны уникальные организации, к которым у вас есть доступ."
        data-testid="profile-organizations"
      >
        <template #actions>
          <q-btn
            outline
            no-caps
            color="primary"
            icon="add_business"
            :label="uniqueOrganisations.length ? 'Добавить организацию' : 'Создать организацию'"
            :to="{ name: 'org_new' }"
          />
        </template>

        <AppState
          v-if="uniqueOrganisations.length === 0"
          state="empty"
          title="Организаций пока нет"
          message="Создайте организацию или дождитесь приглашения от администратора."
        />
        <ul v-else class="entity-list organization-list" data-testid="organization-list">
          <li v-for="item in uniqueOrganisations" :key="item.id" class="entity-row" data-testid="organization-row">
            <q-avatar size="44px" color="primary" text-color="white">
              <img v-if="item.avatar_url" :src="avatarSrc(item.avatar_url)" alt="">
              <span v-else aria-hidden="true">{{ orgInitial(item) }}</span>
            </q-avatar>
            <div class="entity-row__content">
              <strong>{{ organizationName(item) }}</strong>
              <span>ИНН {{ item.inn || 'не указан' }}</span>
              <span>{{ organizationRole(item) }}</span>
            </div>
            <StatusBadge
              :label="isCurrentOrg(item.id) ? 'Выбрана' : 'Доступна'"
              :tone="isCurrentOrg(item.id) ? 'success' : 'neutral'"
              :icon="isCurrentOrg(item.id) ? 'check_circle' : 'domain'"
            />
            <div class="entity-row__actions">
              <q-btn
                v-if="!isCurrentOrg(item.id)"
                outline
                no-caps
                color="primary"
                label="Переключиться"
                @click="selectOrg(item.id)"
              />
              <q-btn flat no-caps color="negative" label="Покинуть" @click="requestLeaveOrganisation(item)" />
            </div>
          </li>
        </ul>
      </PageSection>

      <PageSection
        title="Приглашения"
        description="Входящие приглашения в организации и приглашения, отправленные вами."
        data-testid="profile-invitations"
      >
        <template #actions>
          <q-btn
            flat
            round
            color="primary"
            icon="refresh"
            aria-label="Обновить приглашения"
            :loading="invitationsLoading"
            @click="loadInvitations"
          />
        </template>

        <AppState
          v-if="invitationsLoading && !hasInvitations"
          state="loading"
          title="Загружаем приглашения"
          message="Проверяем входящие и исходящие приглашения."
        />
        <AppState
          v-else-if="invitationsError"
          state="error"
          title="Не удалось загрузить приглашения"
          :message="invitationsError"
          @retry="loadInvitations"
        />
        <AppState
          v-else-if="!hasInvitations"
          state="empty"
          title="Приглашений пока нет"
          message="Новые входящие и исходящие приглашения появятся здесь."
          data-testid="invitations-empty"
        />
        <div v-else class="invitation-groups">
          <section aria-labelledby="incoming-invites-title">
            <h3 id="incoming-invites-title">Входящие</h3>
            <p v-if="incoming_invites.length === 0" class="support-copy">Новых входящих приглашений нет.</p>
            <ul v-else class="entity-list">
              <li v-for="item in incoming_invites" :key="`incoming-${item.id}`" class="entity-row">
                <div class="entity-row__content">
                  <strong>{{ organizationName(item.organisation) }}</strong>
                  <span>Роль: {{ inviteRole(item) }}</span>
                  <span>Пригласил: {{ inviteSender(item) }}</span>
                </div>
                <div class="entity-row__actions">
                  <q-btn color="primary" no-caps label="Принять" @click="acceptInvite(item)" />
                  <q-btn flat no-caps color="negative" label="Отклонить" @click="declineInvite(item)" />
                </div>
              </li>
            </ul>
          </section>
          <section aria-labelledby="outgoing-invites-title">
            <h3 id="outgoing-invites-title">Исходящие</h3>
            <p v-if="outgoing_invites.length === 0" class="support-copy">Активных исходящих приглашений нет.</p>
            <ul v-else class="entity-list">
              <li v-for="item in outgoing_invites" :key="`outgoing-${item.id}`" class="entity-row">
                <div class="entity-row__content">
                  <strong>{{ organizationName(item.organisation) }}</strong>
                  <span>Роль: {{ inviteRole(item) }}</span>
                  <span>Пользователь: {{ item.email || '—' }}</span>
                </div>
                <q-btn flat no-caps color="negative" label="Отозвать" @click="declineInvite(item)" />
              </li>
            </ul>
          </section>
        </div>
      </PageSection>

      <PageSection
        title="Сессии"
        description="Устройства и браузеры, с которых выполнялся вход в аккаунт."
        data-testid="profile-sessions"
      >
        <template #actions>
          <q-btn
            flat
            round
            color="primary"
            icon="refresh"
            aria-label="Обновить список сессий"
            :loading="sessionsLoading"
            @click="loadSessions"
          />
          <q-btn
            outline
            no-caps
            color="negative"
            icon="logout"
            label="Завершить другие сессии"
            :disable="sessionsLoading || otherActiveSessions.length === 0"
            data-testid="terminate-other-sessions"
            @click="requestTerminateOthers"
          />
        </template>

        <AppState
          v-if="sessionsLoading && sessions.length === 0"
          state="loading"
          title="Загружаем сессии"
          message="Проверяем активные и недавние входы."
        />
        <AppState
          v-else-if="sessionsError"
          state="error"
          title="Не удалось загрузить сессии"
          :message="sessionsError"
          @retry="loadSessions"
        />
        <AppState
          v-else-if="sessions.length === 0"
          state="empty"
          title="Сессий пока нет"
          message="После входа информация об устройстве появится здесь."
          data-testid="sessions-empty"
        />
        <template v-else>
          <ul class="session-list" data-testid="session-list">
            <li v-for="session in visibleSessions" :key="session.id" class="session-row" data-testid="session-row">
              <div class="session-row__icon" aria-hidden="true"><q-icon name="devices" /></div>
              <div class="session-row__content">
                <div class="session-row__title">
                  <strong>{{ sessionBrowser(session) }}</strong>
                  <StatusBadge v-if="session.is_current" label="Текущая" tone="success" icon="check_circle" />
                  <StatusBadge
                    :label="sessionStatusLabel(session)"
                    :tone="session.revoked_at ? 'neutral' : (session.is_online ? 'success' : 'neutral')"
                    :icon="session.revoked_at ? 'block' : (session.is_online ? 'wifi' : 'wifi_off')"
                  />
                </div>
                <p>{{ sessionOs(session) }}<span v-if="session.ip_address"> · IP {{ session.ip_address }}</span></p>
                <p v-if="session.location">Местоположение: {{ session.location }}</p>
                <p>Последняя активность: {{ formatDateTime(session.last_seen_at || session.last_connected_at) }}</p>
                <p>Создана: {{ formatDateTime(session.created_at) }} · Истекает: {{ formatDateTime(session.expires_at) }}</p>
                <p v-if="session.revoked_at">Завершена: {{ formatDateTime(session.revoked_at) }}</p>
              </div>
              <div class="session-row__actions">
                <span v-if="session.revoked_at" class="support-copy">Только для аудита</span>
                <q-btn
                  v-else
                  flat
                  no-caps
                  color="negative"
                  :label="session.is_current ? 'Выйти из текущей сессии' : 'Завершить сессию'"
                  :loading="pendingSessionId === session.id"
                  :disable="sessionsLoading || !!pendingSessionId"
                  @click="requestTerminateSession(session)"
                />
              </div>
            </li>
          </ul>
          <q-btn
            v-if="sessions.length > sessionPageSize"
            flat
            no-caps
            color="primary"
            :label="showAllSessions ? 'Показать меньше' : `Показать ещё (${sessions.length - sessionPageSize})`"
            data-testid="sessions-expand"
            @click="showAllSessions = !showAllSessions"
          />
        </template>
      </PageSection>
    </template>

    <q-dialog
      v-model="changePasswordDialog"
      :persistent="passwordPending"
      aria-labelledby="change-password-title"
    >
      <q-card class="password-dialog">
        <q-card-section>
          <h2 id="change-password-title" class="dialog-title">Изменить пароль</h2>
          <p class="support-copy">После изменения пароля другие активные сессии будут завершены.</p>
        </q-card-section>
        <q-card-section class="password-dialog__fields">
          <PasswordInput v-model="currentPassword" label="Текущий пароль" autocomplete="current-password" :disable="passwordPending" />
          <PasswordInput v-model="newPassword" label="Новый пароль" autocomplete="new-password" :disable="passwordPending" />
          <PasswordInput v-model="confirmPassword" label="Повторите новый пароль" autocomplete="new-password" :disable="passwordPending" />
          <p v-if="passwordError" class="inline-status inline-status--error" role="alert">{{ passwordError }}</p>
        </q-card-section>
        <q-card-actions align="right" class="password-dialog__actions">
          <q-btn flat no-caps label="Отмена" :disable="passwordPending" @click="closePasswordDialog" />
          <q-btn
            color="primary"
            no-caps
            label="Сменить пароль"
            :loading="passwordPending"
            :disable="!canChangePassword"
            @click="changePassword"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <ConfirmDialog
      v-model="confirmDialogOpen"
      :title="confirmDialog.title"
      :body="confirmDialog.body"
      :confirm-label="confirmDialog.confirmLabel"
      :pending="confirmPending"
      destructive
      @cancel="closeConfirmDialog"
      @confirm="confirmDestructiveAction"
    />
  </AppPage>
</template>

<script>
import axios from 'axios'
import { apiUrl, mediaUrl } from '../../../config'
import { formatDateTime as formatDisplayDateTime } from '../../../services/dateFormat'
import PasswordInput from '../../../components/auth/PasswordInput.vue'
import {
  AppPage,
  AppState,
  ConfirmDialog,
  PageHeader,
  PageSection,
  StatusBadge
} from '../../../components/ui'

const SESSION_PAGE_SIZE = 4
const MAX_AVATAR_SIZE = 5 * 1024 * 1024
const ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg']

export default {
  name: 'MyProfile',
  components: {
    AppPage,
    AppState,
    ConfirmDialog,
    PageHeader,
    PageSection,
    PasswordInput,
    StatusBadge
  },
  data() {
    return {
      isLoading: true,
      organisations: [],
      incoming_invites: [],
      outgoing_invites: [],
      user_info: null,
      confirmation_code: '',
      is_confirmed: false,
      code_request_sended: false,
      user: { username: '', name: '', surname: '', familyName: '', email: '', avatar: null },
      editedUser: {},
      editMode: false,
      profileSaving: false,
      changePasswordDialog: false,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      passwordPending: false,
      passwordError: '',
      newAvatar: null,
      isAvatarUploading: false,
      avatarStatus: { tone: '', message: '' },
      invitationsLoading: true,
      invitationsError: '',
      sessions: [],
      sessionsLoading: true,
      sessionsError: '',
      pendingSessionId: '',
      showAllSessions: false,
      sessionPageSize: SESSION_PAGE_SIZE,
      confirmDialogOpen: false,
      confirmPending: false,
      confirmDialog: { type: '', target: null, title: '', body: '', confirmLabel: 'Подтвердить' },
      handlers: {}
    }
  },
  computed: {
    fullName() {
      return [this.user.surname, this.user.name || this.user.username, this.user.familyName].filter(Boolean).join(' ')
    },
    userInitials() {
      const source = [this.user.name, this.user.surname].filter(Boolean).join(' ') || this.user.email || '??'
      return source.split(/\s+/).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase()
    },
    uniqueOrganisations() {
      const byId = new Map()
      this.organisations.forEach((item) => {
        if (item && item.id !== undefined && !byId.has(String(item.id))) byId.set(String(item.id), item)
      })
      return Array.from(byId.values())
    },
    hasInvitations() {
      return this.incoming_invites.length > 0 || this.outgoing_invites.length > 0
    },
    orderedSessions() {
      return [...this.sessions].sort((left, right) => Number(Boolean(right.is_current)) - Number(Boolean(left.is_current)))
    },
    visibleSessions() {
      return this.showAllSessions ? this.orderedSessions : this.orderedSessions.slice(0, this.sessionPageSize)
    },
    otherActiveSessions() {
      return this.sessions.filter((session) => !session.is_current && !session.revoked_at)
    },
    canChangePassword() {
      return !this.passwordPending && Boolean(this.currentPassword && this.newPassword && this.confirmPassword)
    }
  },
  watch: {
    '$route.query.section': {
      immediate: true,
      handler(section) {
        if (section === 'organizations') this.scrollToOrganizations()
      }
    }
  },
  created() {
    this.handlers = {
      acceptedInvite: this.onAcceptedInvite,
      leftOrganisation: this.onLeftOrganisation,
      invitations: this.onInvitations,
      userInfo: this.onUserInfo,
      confirmationResent: this.onConfirmationResent,
      editedUser: this.onEditedUser,
      confirmedUser: this.onConfirmedUser,
      passwordChanged: this.onPasswordChanged,
      sessions: this.onSessions,
      terminatedSession: this.onTerminatedSession,
      terminatedOthers: this.onTerminatedOthers,
      sessionInvalidated: this.onSessionInvalidated,
      connectionReady: this.requestProfileData
    }
    const eventMap = {
      didAcceptInvite: 'acceptedInvite',
      didReceiveLeaveOrganisation: 'leftOrganisation',
      onGetMyInvitesList: 'invitations',
      didReceiveUserInfo: 'userInfo',
      onResendConfirmationCode: 'confirmationResent',
      didEditUserInfo: 'editedUser',
      didConfirmUser: 'confirmedUser',
      onChangeUserPassword: 'passwordChanged',
      onGetMySessions: 'sessions',
      onTerminateMySession: 'terminatedSession',
      onTerminateOtherSessions: 'terminatedOthers',
      sessionInvalidated: 'sessionInvalidated'
    }
    Object.entries(eventMap).forEach(([eventName, key]) => this.emitter.on(eventName, this.handlers[key]))

    if (this.connection.isAuthenticated) this.requestProfileData()
    else this.emitter.on('connectionReady', this.handlers.connectionReady)
  },
  beforeUnmount() {
    const eventMap = {
      didAcceptInvite: 'acceptedInvite', didReceiveLeaveOrganisation: 'leftOrganisation', onGetMyInvitesList: 'invitations',
      didReceiveUserInfo: 'userInfo', onResendConfirmationCode: 'confirmationResent', didEditUserInfo: 'editedUser',
      didConfirmUser: 'confirmedUser', onChangeUserPassword: 'passwordChanged', onGetMySessions: 'sessions',
      onTerminateMySession: 'terminatedSession', onTerminateOtherSessions: 'terminatedOthers', sessionInvalidated: 'sessionInvalidated',
      connectionReady: 'connectionReady'
    }
    Object.entries(eventMap).forEach(([eventName, key]) => this.emitter.off(eventName, this.handlers[key]))
  },
  methods: {
    requestProfileData() {
      this.connection.getUserInfo()
      this.loadInvitations()
      this.loadSessions()
    },
    onUserInfo(data) {
      this.user_info = data
      this.organisations = Array.isArray(data.organisations) ? data.organisations : []
      this.is_confirmed = Boolean(data.is_confirmed)
      const email = data.email || ''
      this.user = {
        username: email.split('@')[0],
        name: data.first_name || data.name || '',
        surname: data.last_name || '',
        familyName: data.surname || data.familyName || '',
        email,
        avatar: data.avatar_url || data.avatar || null
      }
      this.editedUser = { ...this.user }
      this.isLoading = false
      this.scrollToOrganizations()
    },
    loadInvitations() {
      if (!this.connection || !this.connection.isAuthenticated) return
      this.invitationsLoading = true
      this.invitationsError = ''
      this.connection.getMyInvitesList(this.connection.makeRequestId('get-my-invites'))
    },
    onInvitations(data) {
      this.invitationsLoading = false
      if (data.error !== undefined) {
        this.invitationsError = 'Повторите запрос. Если ошибка сохранится, обратитесь к администратору.'
        return
      }
      this.incoming_invites = Array.isArray(data.incoming_invites) ? data.incoming_invites : []
      this.outgoing_invites = Array.isArray(data.outgoing_invites) ? data.outgoing_invites : []
    },
    loadSessions() {
      if (!this.connection || !this.connection.isAuthenticated) return
      this.sessionsLoading = true
      this.sessionsError = ''
      this.connection.getMySessions(this.connection.makeRequestId('get-my-sessions'))
    },
    onSessions(data) {
      this.sessionsLoading = false
      if (data.error !== undefined) {
        this.sessionsError = this.sessionErrorMessage(data.error)
        return
      }
      this.sessions = Array.isArray(data.sessions) ? data.sessions : []
      this.showAllSessions = false
    },
    requestTerminateSession(session) {
      this.confirmDialog = {
        type: 'session', target: session,
        title: session.is_current ? 'Выйти из текущей сессии' : 'Завершить сессию',
        body: session.is_current ? 'Вы будете перенаправлены на страницу входа.' : 'Это устройство потеряет доступ к аккаунту.',
        confirmLabel: session.is_current ? 'Выйти' : 'Завершить'
      }
      this.confirmDialogOpen = true
    },
    requestTerminateOthers() {
      this.confirmDialog = {
        type: 'other-sessions', target: null, title: 'Завершить другие сессии',
        body: 'Все другие устройства и вкладки потеряют доступ к аккаунту.', confirmLabel: 'Завершить все'
      }
      this.confirmDialogOpen = true
    },
    requestLeaveOrganisation(item) {
      this.confirmDialog = {
        type: 'leave-organization', target: item, title: 'Покинуть организацию',
        body: `Вы потеряете доступ к документам и ролям организации «${this.organizationName(item)}».`, confirmLabel: 'Покинуть'
      }
      this.confirmDialogOpen = true
    },
    confirmDestructiveAction() {
      this.confirmPending = true
      if (this.confirmDialog.type === 'session') {
        this.pendingSessionId = this.confirmDialog.target.id
        this.connection.terminateMySession(this.connection.makeRequestId('terminate-session'), this.confirmDialog.target.id)
      } else if (this.confirmDialog.type === 'other-sessions') {
        this.connection.terminateOtherSessions(this.connection.makeRequestId('terminate-other-sessions'))
      } else if (this.confirmDialog.type === 'leave-organization') {
        this.leaveOrg(this.confirmDialog.target.id)
      }
    },
    closeConfirmDialog() {
      if (!this.confirmPending) this.confirmDialogOpen = false
    },
    finishConfirmDialog() {
      this.confirmPending = false
      this.confirmDialogOpen = false
      this.pendingSessionId = ''
    },
    onTerminatedSession(data) {
      this.finishConfirmDialog()
      if (data.error !== undefined) {
        this.sessionsError = this.sessionErrorMessage(data.error)
        this.loadSessions()
        return
      }
      if (data.terminated_current) {
        this.connection.logout(this.$q, false)
        this.$router.push({ name: 'signin' })
        return
      }
      this.loadSessions()
    },
    onTerminatedOthers(data) {
      this.finishConfirmDialog()
      if (data.error !== undefined) {
        this.sessionsError = this.sessionErrorMessage(data.error)
        return
      }
      this.$q.notify({ color: 'positive', icon: 'check', message: 'Другие сессии завершены' })
      this.loadSessions()
    },
    sessionErrorMessage(error) {
      return ({
        access_denied: 'Нет доступа к этой сессии.', session_not_found: 'Сессия не найдена.',
        session_already_revoked: 'Сессия уже завершена.', session_revoked: 'Текущая сессия истекла.'
      })[error] || 'Не удалось выполнить действие с сессией.'
    },
    onSessionInvalidated() {
      this.connection.logout(this.$q, false)
      this.$router.push({ name: 'signin' })
    },
    toggleEditMode() {
      if (!this.editMode) {
        this.editedUser = { ...this.user }
        this.editMode = true
        return
      }
      this.profileSaving = true
      this.connection.editUserInfo({
        last_name: this.editedUser.surname,
        first_name: this.editedUser.name,
        surname: this.editedUser.familyName
      })
    },
    cancelEdit() {
      this.editedUser = { ...this.user }
      this.editMode = false
    },
    onEditedUser(data) {
      this.profileSaving = false
      if (data && data.error !== undefined) {
        this.$q.notify({ color: 'negative', icon: 'warning', message: 'Не удалось сохранить личные данные' })
        return
      }
      this.user = { ...this.editedUser }
      this.editMode = false
      this.$q.notify({ color: 'positive', icon: 'check', message: 'Личные данные сохранены' })
    },
    openPasswordDialog() {
      this.passwordError = ''
      this.changePasswordDialog = true
    },
    closePasswordDialog() {
      if (this.passwordPending) return
      this.changePasswordDialog = false
      this.currentPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.passwordError = ''
    },
    changePassword() {
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = 'Пароли не совпадают.'
        return
      }
      if (this.newPassword.length < 8) {
        this.passwordError = 'Новый пароль должен быть не короче 8 символов.'
        return
      }
      this.passwordError = ''
      this.passwordPending = true
      this.connection.changeUserPassword(this.connection.makeRequestId('change-password'), this.currentPassword, this.newPassword)
    },
    onPasswordChanged(data) {
      this.passwordPending = false
      if (data.error !== undefined) {
        this.passwordError = ({
          invalid_password: 'Текущий пароль введён неверно.', invalid_data: 'Проверьте заполненные поля.',
          'weak-password': 'Новый пароль должен быть не короче 8 символов.', password_reused: 'Новый пароль должен отличаться от текущего.'
        })[data.error] || 'Не удалось изменить пароль.'
        return
      }
      this.closePasswordDialog()
      this.$q.notify({ color: 'positive', icon: 'check', message: 'Пароль изменён. Другие сессии завершены.' })
      this.loadSessions()
    },
    onAvatarSelected(file) {
      if (!file) return
      if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
        this.avatarStatus = { tone: 'error', message: this.avatarErrorMessage('invalid_file_type') }
        this.newAvatar = null
        return
      }
      if (file.size > MAX_AVATAR_SIZE) {
        this.avatarStatus = { tone: 'error', message: this.avatarErrorMessage('file_too_large') }
        this.newAvatar = null
        return
      }
      const formData = new FormData()
      formData.append('file', file)
      this.appendAvatarAuth(formData)
      this.isAvatarUploading = true
      this.avatarStatus = { tone: 'info', message: 'Загружаем изображение…' }
      axios.post(`${apiUrl}/upload/avatar/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((response) => {
          if (response.data && response.data.success === 1) {
            this.user.avatar = response.data.url
            this.avatarStatus = { tone: 'success', message: 'Аватар обновлён.' }
          } else this.avatarStatus = { tone: 'error', message: this.avatarErrorMessage(response.data && response.data.error) }
        })
        .catch(() => { this.avatarStatus = { tone: 'error', message: this.avatarErrorMessage('internal') } })
        .finally(() => { this.isAvatarUploading = false; this.newAvatar = null })
    },
    deleteAvatar() {
      if (!this.connection.token || !this.connection.token_uuid) this.connection.loadCredentials(this.$q)
      this.isAvatarUploading = true
      this.avatarStatus = { tone: 'info', message: 'Удаляем изображение…' }
      axios.post(`${apiUrl}/upload/avatar/delete/`, { token: this.connection.token, token_uuid: this.connection.token_uuid })
        .then((response) => {
          if (response.data && response.data.success === 1) {
            this.user.avatar = null
            this.avatarStatus = { tone: 'success', message: 'Аватар удалён.' }
          } else this.avatarStatus = { tone: 'error', message: this.avatarErrorMessage(response.data && response.data.error) }
        })
        .catch(() => { this.avatarStatus = { tone: 'error', message: this.avatarErrorMessage('internal') } })
        .finally(() => { this.isAvatarUploading = false; this.newAvatar = null })
    },
    appendAvatarAuth(formData) {
      if (!this.connection.token || !this.connection.token_uuid) this.connection.loadCredentials(this.$q)
      formData.append('token', this.connection.token || '')
      formData.append('token_uuid', this.connection.token_uuid || '')
    },
    triggerAvatarUpload() {
      if (this.$refs.avatarInput) this.$refs.avatarInput.pickFiles()
    },
    avatarErrorMessage(code) {
      return ({
        invalid_file_type: 'Можно загрузить только PNG или JPG.', file_too_large: 'Файл больше 5 МБ.',
        access_denied: 'Недостаточно прав для изменения аватара.', no_file_attached: 'Файл не выбран.',
        bad_request: 'Некорректный запрос.', internal: 'Не удалось обработать аватар.'
      })[code] || 'Не удалось обработать аватар.'
    },
    avatarSrc(url) {
      if (!url) return ''
      if (/^(https?:|data:)/.test(url)) return url
      return `${mediaUrl}${url.startsWith('/') ? url : `/${url}`}`
    },
    organizationName(item) {
      return (item && (item.display_name || item.short_name || item.name)) || 'Организация без названия'
    },
    orgInitial(item) {
      return this.organizationName(item).charAt(0).toUpperCase()
    },
    organizationRole(item) {
      if (item && item.role) return item.role.role || item.role.name || item.role
      if (this.user_info && item && item.admin && item.admin.id === this.user_info.id) return 'Администратор'
      return (this.user_info && this.user_info.role && this.user_info.role.role) || 'Участник'
    },
    inviteRole(item) {
      return (item.role && (item.role.role || item.role.name)) || 'Не указана'
    },
    inviteSender(item) {
      return (item.invited_user && (item.invited_user.email || item.invited_user.display_name)) || item.invited_by || '—'
    },
    isCurrentOrg(orgId) {
      return String(this.connection.selectedOrgId(this.$q)) === String(orgId)
    },
    selectOrg(orgId) {
      this.connection.updateOrgId(this.$q, orgId)
      this.connection.getOrganisationInfo(this.connection.makeRequestId('profile-org-info'))
      this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('profile-org-role'))
    },
    leaveOrg(orgId) {
      if (this.isCurrentOrg(orgId)) {
        const replacement = this.uniqueOrganisations.find((item) => String(item.id) !== String(orgId))
        this.connection.updateOrgId(this.$q, replacement ? replacement.id : -1)
        if (replacement) {
          this.connection.getOrganisationInfo(this.connection.makeRequestId('profile-org-info'))
          this.connection.getUserInfoAcrossOrg(this.connection.makeRequestId('profile-org-role'))
        }
      }
      this.connection.leaveOrganisation(this.connection.makeRequestId('leave-org'), orgId)
    },
    onLeftOrganisation() {
      this.finishConfirmDialog()
      this.connection.getUserInfo()
    },
    acceptInvite(invite) {
      this.connection.acceptInvite(this.connection.makeRequestId('accept-invite'), invite.token || invite.id)
    },
    declineInvite(invite) {
      this.connection.declineInvite(this.connection.makeRequestId('decline-invite'), invite.token || invite.id)
      this.incoming_invites = this.incoming_invites.filter((item) => item.id !== invite.id)
      this.outgoing_invites = this.outgoing_invites.filter((item) => item.id !== invite.id)
    },
    onAcceptedInvite(data) {
      if (data.org_id) this.connection.updateOrgId(this.$q, data.org_id)
      this.connection.getUserInfo()
      this.loadInvitations()
    },
    sendConfirmationCode() {
      const code = Number(String(this.confirmation_code || '').replace(/\s/g, ''))
      if (!code) return
      this.code_request_sended = true
      this.connection.confirmUser(this.connection.makeRequestId('confirm-user'), code)
    },
    resendConfirmationCode() {
      this.code_request_sended = true
      this.connection.resendConfirmationCode(this.connection.makeRequestId('resend-confirmation'))
    },
    onConfirmationResent() {
      this.code_request_sended = false
      this.$q.notify({ color: 'positive', icon: 'mail', message: 'Новый код отправлен' })
    },
    onConfirmedUser(data) {
      this.code_request_sended = false
      if (data.error) {
        this.$q.notify({ color: 'negative', icon: 'warning', message: 'Неверный код подтверждения' })
        return
      }
      this.confirmation_code = ''
      this.is_confirmed = true
      this.$q.notify({ color: 'positive', icon: 'check', message: 'Аккаунт подтверждён' })
    },
    formatDateTime(value) {
      return value ? formatDisplayDateTime(value, '—') : '—'
    },
    sessionBrowser(session) {
      const browser = session.browser_name || session.device_name || 'Неизвестный браузер'
      return session.browser_version ? `${browser} ${session.browser_version}` : browser
    },
    sessionOs(session) {
      const os = session.os_name || 'ОС не определена'
      return session.os_version ? `${os} ${session.os_version}` : os
    },
    sessionStatusLabel(session) {
      if (session.revoked_at) return 'Завершена'
      return session.is_online ? 'В сети' : 'Не в сети'
    },
    async scrollToOrganizations() {
      if (this.$route.query.section !== 'organizations' || this.isLoading) return
      await this.$nextTick()
      const component = this.$refs.organizationsSection
      const element = component && component.$el
      if (element) element.scrollIntoView({ block: 'start' })
    }
  }
}
</script>

<style scoped>
.identity-layout {
  display: grid;
  grid-template-columns: minmax(13rem, 15rem) minmax(0, 1fr);
  gap: var(--sid-space-6);
}

.avatar-control,
.identity-details,
.avatar-control__actions,
.password-dialog__fields {
  display: flex;
  flex-direction: column;
}

.avatar-control { align-items: flex-start; gap: var(--sid-space-3); }
.avatar-control__actions { align-items: flex-start; gap: var(--sid-space-2); }
.avatar-control__input { position: absolute; width: 1px; height: 1px; overflow: hidden; opacity: 0; pointer-events: none; }
.identity-details { min-width: 0; gap: var(--sid-space-4); }
.identity-summary { min-width: 0; }
.identity-summary__name, .identity-summary__email { display: block; overflow-wrap: anywhere; }
.identity-summary__name { font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
.identity-summary__email, .support-copy { color: var(--sid-color-text-secondary); }
.identity-form { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-3); }

.definition-list { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin: 0; }
.definition-list div { min-width: 0; }
.definition-list dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.definition-list dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
.section-actions { display: flex; flex-wrap: wrap; gap: var(--sid-space-2); }

.confirmation-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(17rem, 28rem);
  gap: var(--sid-space-5);
  margin-top: var(--sid-space-5);
  padding: var(--sid-space-4);
  border: 1px solid var(--sid-color-warning);
  border-radius: var(--sid-radius-large);
  background: var(--sid-color-warning-surface);
}
.confirmation-panel h3, .confirmation-panel p { margin: 0; }
.confirmation-panel p { margin-top: var(--sid-space-1); }
.confirmation-panel__form { display: flex; align-items: flex-start; flex-wrap: wrap; gap: var(--sid-space-2); }
.confirmation-panel__form .q-field { flex: 1 1 12rem; }

.entity-list, .session-list { margin: 0; padding: 0; list-style: none; }
.entity-row, .session-row { display: flex; align-items: center; min-width: 0; gap: var(--sid-space-4); padding: var(--sid-space-4) 0; border-bottom: 1px solid var(--sid-color-line); }
.entity-row:first-child, .session-row:first-child { padding-top: 0; }
.entity-row:last-child, .session-row:last-child { padding-bottom: 0; border-bottom: 0; }
.entity-row__content, .session-row__content { flex: 1 1 auto; min-width: 0; }
.entity-row__content strong, .entity-row__content span { display: block; overflow-wrap: anywhere; }
.entity-row__content span { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.entity-row__actions { display: flex; flex: 0 0 auto; flex-wrap: wrap; justify-content: flex-end; gap: var(--sid-space-2); }

.invitation-groups { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); }
.invitation-groups h3 { margin: 0 0 var(--sid-space-3); font-size: var(--sid-font-size-body); }
.invitation-groups .entity-row { align-items: flex-start; }

.session-row { align-items: flex-start; }
.session-row__icon { display: grid; place-items: center; flex: 0 0 44px; width: 44px; height: 44px; border-radius: var(--sid-radius-medium); background: var(--sid-color-surface-subtle); color: var(--sid-color-text-secondary); font-size: 1.5rem; }
.session-row__title { display: flex; flex-wrap: wrap; align-items: center; gap: var(--sid-space-2); }
.session-row__content p { margin: var(--sid-space-1) 0 0; color: var(--sid-color-text-secondary); overflow-wrap: anywhere; }
.session-row__actions { flex: 0 0 auto; }

.support-copy { margin: 0; overflow-wrap: anywhere; }
.inline-status { margin: 0; font-size: var(--sid-font-size-label); overflow-wrap: anywhere; }
.inline-status--success { color: var(--sid-color-success); }
.inline-status--info { color: var(--sid-color-info); }
.inline-status--error { color: var(--sid-color-danger); }
.dialog-title { margin: 0; font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
.password-dialog { width: min(32rem, calc(100vw - 2 * var(--sid-space-4))); max-width: 100%; border-radius: var(--sid-radius-large); }
.password-dialog__fields { gap: var(--sid-space-3); }
.password-dialog__actions { gap: var(--sid-space-2); padding: 0 var(--sid-space-4) var(--sid-space-4); }

@media (max-width: 767px) {
  .identity-layout, .confirmation-panel, .invitation-groups { grid-template-columns: minmax(0, 1fr); }
  .identity-form, .definition-list { grid-template-columns: minmax(0, 1fr); }
  .entity-row, .session-row { align-items: flex-start; flex-wrap: wrap; }
  .entity-row__content, .session-row__content { flex-basis: calc(100% - 60px); }
  .entity-row > .status-badge { margin-left: 60px; }
  .entity-row__actions, .session-row__actions { width: 100%; justify-content: flex-start; padding-left: 60px; }
  .invitation-groups .entity-row__actions { padding-left: 0; }
  .password-dialog__actions { align-items: stretch; flex-direction: column-reverse; }
  .password-dialog__actions :deep(.q-btn), .section-actions :deep(.q-btn), .avatar-control__actions :deep(.q-btn) { width: 100%; }
  .section-actions, .avatar-control__actions { width: 100%; }
}

@media (max-width: 399px) {
  .entity-row__content, .session-row__content { flex-basis: 100%; }
  .entity-row > .status-badge, .entity-row__actions, .session-row__actions { margin-left: 0; padding-left: 0; }
}
</style>
